import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Typography, Chip, IconButton } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NodeSelect from './NodeSelect';
import NodeAddButton from './NodeAddButton';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeMergeLogicBuilder — Merge paths logic builder (OR/AND groups).
 * Used by: Merge Paths.
 *
 * Manages groups of component paths that must complete before the merge continues.
 * Each group is an OR/AND condition with selected component chips.
 */

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.md,
  width: '100%',
});

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  userSelect: 'none',
}));

const GroupCard = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  padding: NODE_UI.spacing.md,
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.sm,
  position: 'relative',
}));

const GroupHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const GroupLabel = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
}));

const ChipsContainer = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
});

const PathChip = styled(Chip)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  height: 24,
  borderRadius: NODE_UI.borderRadiusSm,
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  padding: 3,
  color: theme.palette.text.secondary,
  '&:hover': { color: theme.palette.error.main },
}));

const LOGIC_OPTIONS = [
  { value: 'OR', label: 'Any path (OR)' },
  { value: 'AND', label: 'All paths (AND)' },
];

const NodeMergeLogicBuilder = React.memo(function NodeMergeLogicBuilder({
  label = 'Merge Condition',
  groups = [],
  onGroupsChange,
  availablePaths = [],
  addGroupLabel = 'Add Group',
  sx,
}) {
  const handleAddGroup = useCallback(() => {
    onGroupsChange?.([...groups, { logic: 'OR', paths: [] }]);
  }, [groups, onGroupsChange]);

  const handleUpdateGroup = useCallback((index, updates) => {
    const next = [...groups];
    next[index] = { ...next[index], ...updates };
    onGroupsChange?.(next);
  }, [groups, onGroupsChange]);

  const handleDeleteGroup = useCallback((index) => {
    onGroupsChange?.(groups.filter((_, i) => i !== index));
  }, [groups, onGroupsChange]);

  const handleRemovePath = useCallback((groupIndex, pathIndex) => {
    const next = [...groups];
    next[groupIndex] = {
      ...next[groupIndex],
      paths: next[groupIndex].paths.filter((_, i) => i !== pathIndex),
    };
    onGroupsChange?.(next);
  }, [groups, onGroupsChange]);

  return (
    <Root onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation} sx={sx}>
      {label && <Label>{label}</Label>}

      {groups.map((group, groupIndex) => (
        <GroupCard key={groupIndex}>
          <GroupHeader>
            <GroupLabel>Group {groupIndex + 1}</GroupLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <NodeSelect
                value={group.logic}
                onChange={(val) => handleUpdateGroup(groupIndex, { logic: val })}
                options={LOGIC_OPTIONS}
                sx={{ maxWidth: 160 }}
              />
              {groups.length > 1 && (
                <DeleteButton size="small" onClick={(e) => { stopPropagation(e); handleDeleteGroup(groupIndex); }}>
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </DeleteButton>
              )}
            </div>
          </GroupHeader>
          <ChipsContainer>
            {group.paths.map((path, pathIndex) => (
              <PathChip
                key={path.id || pathIndex}
                label={path.name || path.label || `Path ${pathIndex + 1}`}
                onDelete={() => handleRemovePath(groupIndex, pathIndex)}
                size="small"
              />
            ))}
            {group.paths.length === 0 && (
              <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: 'text.secondary' }}>
                No paths selected
              </Typography>
            )}
          </ChipsContainer>
        </GroupCard>
      ))}

      <NodeAddButton label={addGroupLabel} onClick={handleAddGroup} />
    </Root>
  );
});

export default NodeMergeLogicBuilder;
